/** Resolve dependency row ids before opening the metadata editor (same rules as Workspace). */

const CANONICAL_METADATA_ITEM_ID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export type DependencyOpenItem = {
  name: string;
  id?: string;
  metadata_id?: string;
  metadata_type?: string;
  metadataType?: string;
};

export function normalizeDependencyOpenItem(raw: DependencyOpenItem): DependencyOpenItem {
  return {
    name: (raw.name || "").trim(),
    id: String(raw.id || raw.metadata_id || "").trim(),
    metadata_type: raw.metadata_type || raw.metadataType,
  };
}

export function isCanonicalMetadataItemId(id: string): boolean {
  return CANONICAL_METADATA_ITEM_ID_RE.test(id);
}

export function isArtifactSessionId(id: string): boolean {
  return id.startsWith("art_");
}

/** Tab / GraphQL keys that do not need a by-name lookup (UUID or session artifact). */
export function isEditorSafeMetadataItemId(id: string | undefined | null): boolean {
  if (!id) return false;
  return isCanonicalMetadataItemId(id) || isArtifactSessionId(id);
}

/**
 * Returns a canonical metadata UUID or `art_…` id for `onOpenFileInEditor`.
 * Salesforce 15/18-char ids, class names, and stale ids fall back to by-name lookup.
 */
export async function resolveDependencyItemForEditor(
  raw: DependencyOpenItem,
  orgId: string | undefined,
): Promise<{ id: string; name: string; metadataType: string } | null> {
  const item = normalizeDependencyOpenItem(raw);
  const name = item.name;
  if (!name) return null;

  let itemId = item.id || "";

  if (!isEditorSafeMetadataItemId(itemId)) {
    if (!orgId) return null;
    try {
      const { getMetadataItemByName } = await import("../../devAgentService");
      const resolved = await getMetadataItemByName(name, orgId, true);
      itemId = resolved?.id || "";
    } catch {
      return null;
    }
  }

  if (!itemId) return null;

  return {
    id: itemId,
    name,
    metadataType: item.metadata_type || "ApexClass",
  };
}

export function isSameDependencySnapshot(
  a: { target: string; captured_at?: string },
  b: { target: string; captured_at?: string },
): boolean {
  if (a.target !== b.target) return false;
  if (a.captured_at && b.captured_at) return a.captured_at === b.captured_at;
  return true;
}
